import React from 'react';
import { Search, Upload, Plus, Save, Trash2, Loader2, X, Copy } from 'lucide-react';
import DownloadExcel from '../assets/DownloadExcel';
import { useTranslation } from 'react-i18next';

interface TableActionsToolbarProps {
  neutralControls?: boolean;
  searchInput: string;
  setSearchInput: (value: string) => void;
  isSearchOpen: boolean;
  setIsSearchOpen: (value: boolean) => void;
  handleSearch: () => void;
  clearSearch: () => void;
  searchPlaceholder?: string;
  fileInputRef?: React.RefObject<HTMLInputElement | null>;
  handleExcelUpload?: (file: File) => void;
  handleDownloadExcel?: () => void;

  triggerFileUpload?: () => void;
  showPlusButton?: boolean;
  onPlusClick?: () => void;
  showExcelButtons?: boolean;
  leftContent?: React.ReactNode;
  customUploadButton?: React.ReactNode; // Custom upload component (e.g., UploadExcel for frontend parsing)
  // Save button props
  showSaveButton?: boolean;
  onSave?: () => void;
  isDirty?: boolean;
  isSaving?: boolean;
  /** Override the i18n key used for the Save button tooltip when dirty. Defaults to 'common.saveChanges'. */
  saveTooltipKey?: string;
  /** When false, Save and Plus (add) are hidden/disabled. Default true. */
  canWrite?: boolean;
  searchAlwaysVisible?: boolean;
  /** When false, the search icon and the sliding search box are not rendered. Default true. */
  showSearch?: boolean;
  onCopyWarehouseQuantities?: () => void;
  canCopyWarehouseQuantities?: boolean;
}

/**
 * Generic table actions toolbar component
 * Reusable across Partner Pricing, Material Documents, Non-Material Documents, Work Orders, and Planning
 * Includes optional Plus button, Excel upload/download, and search with sliding input
 */
const TableActionsToolbar: React.FC<TableActionsToolbarProps> = ({
  searchInput,
  setSearchInput,
  isSearchOpen,
  setIsSearchOpen,
  handleSearch,
  clearSearch,
  searchPlaceholder,
  fileInputRef,
  handleExcelUpload,
  handleDownloadExcel,
  triggerFileUpload,
  showPlusButton = true,
  onPlusClick,
  showExcelButtons = true,
  leftContent,
  customUploadButton,
  showSaveButton = false,
  onSave,
  isDirty = false,
  isSaving = false,
  saveTooltipKey,
  canWrite = true,
  searchAlwaysVisible = false,
  showSearch = true,
  onCopyWarehouseQuantities,
  canCopyWarehouseQuantities = false,
  neutralControls = false,
}) => {
  const { t } = useTranslation();
  const neutralButtonClass = `flex h-8 w-8 shrink-0 items-center justify-center rounded-md border
    border-gray-300 bg-white text-gray-700 shadow-sm transition-colors hover:bg-gray-50
    hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50`;


  return (
    <div className="flex items-center gap-2">
      {/* Optional left content (e.g., warehouse dropdown for material documents) */}
      {leftContent}

      {/* Save button - shows when enabled and canWrite, disabled state when not dirty */}
      {showSaveButton && canWrite && (
        <button
          onClick={onSave}
          disabled={!isDirty || isSaving}
          className={`group relative flex items-center justify-center p-1.5 rounded-lg shadow-md transition-all duration-200 focus:outline-none focus:ring-2 bg-white ${
            isDirty && !isSaving
              ? 'hover:shadow-lg hover:scale-105 hover:opacity-90 cursor-pointer'
              : 'opacity-50 cursor-not-allowed'
          }`}
          style={{
            border: isDirty && !isSaving
              ? '2px solid #10B981' // Green when dirty (emerald-500)
              : '2px solid var(--color-primary)'
          }}
          title={isDirty ? (t(saveTooltipKey || 'common.saveChanges') || 'Save changes') : (t('common.noChangesToSave') || 'No changes to save')}
        >
          {isSaving ? (
            <Loader2 size={14} strokeWidth={2.5} className="animate-spin" style={{ color: 'var(--color-primary)' }} />
          ) : (
            <Save size={14} strokeWidth={2.5} style={{ color: isDirty ? '#10B981' : 'var(--color-primary)' }} />
          )}
        </button>
      )}

      {/* Plus icon button - add new row (requires write) */}
      {showPlusButton && canWrite && (
        <button
          onClick={onPlusClick}
          className={neutralControls ? neutralButtonClass : "group relative flex items-center justify-center p-1.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 hover:opacity-90 focus:outline-none focus:ring-2 bg-white"}
          style={neutralControls ? undefined : { border: '2px solid var(--color-primary)' }}
          title="Add New Row"
        >
          <Plus size={14} strokeWidth={2.5} style={neutralControls ? undefined : { color: 'var(--color-primary)' }} />
        </button>
      )}

      {/* Excel buttons */}
      {showExcelButtons && (
        <>
          {/* Custom upload button (e.g., UploadExcel for frontend parsing) or default file input */}
          {canWrite && (customUploadButton ? (
            customUploadButton
          ) : (
            fileInputRef && handleExcelUpload && triggerFileUpload && (
              <>
                <button
                  onClick={triggerFileUpload}
                  className="group relative flex items-center justify-center p-1.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 hover:opacity-90 focus:outline-none focus:ring-2 overflow-hidden bg-white"
                  style={{ border: '2px solid var(--color-primary)' }}
                  title={t('report.uploadExcel')}
                >
                  <Upload size={14} strokeWidth={2.5} style={{ color: 'var(--color-primary)' }} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleExcelUpload(file);
                    }
                    e.target.value = ''; // Reset for re-upload
                  }}
                  className="hidden"
                />
              </>
            )
          ))}
          {/* Download button */}
          {handleDownloadExcel && <DownloadExcel onDownload={handleDownloadExcel} />}
        </>
      )}

      {/* Search icon button */}
      {showSearch && (
      <button
        onClick={() => setIsSearchOpen(!isSearchOpen)}
        className={neutralControls
          ? (searchAlwaysVisible ? 'hidden' : neutralButtonClass)
          : `${searchAlwaysVisible ? 'hidden' : 'flex'} group relative items-center justify-center p-1.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 hover:opacity-90 focus:outline-none focus:ring-2 bg-white`}
        style={neutralControls ? undefined : { border: '2px solid var(--color-primary)' }}
        title="Search"
      >
        <Search size={14} strokeWidth={2.5} style={neutralControls ? undefined : { color: 'var(--color-primary)' }} />
      </button>
      )}

      {onCopyWarehouseQuantities && (
        <button
          type="button"
          onClick={onCopyWarehouseQuantities}
          disabled={!canCopyWarehouseQuantities || isSaving}
          className={`group relative flex items-center justify-center p-1.5 rounded-lg shadow-md transition-all duration-200 focus:outline-none focus:ring-2 bg-white ${canCopyWarehouseQuantities && !isSaving ? 'hover:shadow-lg hover:scale-105 hover:opacity-90' : 'opacity-50 cursor-not-allowed'}`}
          style={{ border: '2px solid var(--color-primary)' }}
          title={t('nestedTable.copyWarehouseQuantities', 'Копирај количини од магацин')}
          aria-label={t('nestedTable.copyWarehouseQuantities', 'Копирај количини од магацин')}
        >
          <Copy size={14} strokeWidth={2.5} style={{ color: 'var(--color-primary)' }} />
        </button>
      )}

      {/* Fancy Sliding Search Input */}
      {showSearch && (
      <div
        className={searchAlwaysVisible
          ? 'flex min-w-[220px] flex-1 max-w-sm ml-2 opacity-100'
          : `overflow-hidden transition-all duration-300 ease-in-out ${isSearchOpen ? 'max-w-md opacity-100 ml-2' : 'max-w-0 opacity-0 ml-0'}`}
      >
        <div className={neutralControls
          ? 'flex h-8 items-center gap-2 whitespace-nowrap rounded-md border border-gray-300 bg-white px-2 shadow-sm focus-within:bg-blue-50 focus-within:ring-2 focus-within:ring-blue-500/50'
          : 'flex items-center gap-2 bg-white rounded-lg px-2 py-1.5 shadow-lg border border-gray-200 whitespace-nowrap'}>
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              } else if (e.key === 'Escape') {
                setIsSearchOpen(false);
                clearSearch();
              }
            }}
            placeholder={searchPlaceholder || t('search.search')}
            className="flex-1 outline-none text-sm text-gray-700 placeholder-gray-400 bg-transparent w-48"
            autoFocus={isSearchOpen}
          />
          {searchInput && (
            <button
              onClick={clearSearch}
              className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default TableActionsToolbar;
